/**
 * @file        src/pages/Login.jsx
 * @description Email + password login. On success, axios stores the access
 *              token in memory and the backend sets the httpOnly refresh cookie.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-31 Claude — Logo comes from the tenant's Site Settings (resolved
 *                          from the domain) instead of the bundled PNG, which is
 *                          now only the fallback. Auth flow untouched.
 *   - 2026-08-04 Claude — Staff accounts (Staff Management) now sign in here
 *                          too, with their Login ID instead of an email — the
 *                          field is no longer type="email" (that rejected any
 *                          non-email-shaped value before submit) and the label/
 *                          placeholder cover both cases. Backend dispatch is
 *                          unchanged: same POST /auth/login body shape.
 *   - 2026-08-06 Claude — Email placeholder no longer hardcodes
 *                          "neonholidays.com" (this console is multi-tenant);
 *                          uses a generic "you@company.com" example instead.
 *   - 2026-09-16 Claude — Label is "Email" again: staff sign in with their email
 *                          now (migration 063 dropped `staff.login_id`), so there
 *                          is no second identifier for it to cover. The input
 *                          stays type="text" on purpose — a legacy staff member
 *                          typing their old login ID should reach the server and
 *                          get the same BAD_CREDENTIALS as any wrong guess,
 *                          rather than be stopped by the browser's own email
 *                          validation with a message that explains nothing.
 *   - 2026-09-16 Claude — Password field is now <PasswordInput>, which adds a
 *                          reveal toggle. A mistyped password was invisible until
 *                          a failed sign-in, which reads the same as a wrong
 *                          account. Nothing about the auth call changed.
 */
import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth.store';
import { useBranding } from '../lib/useBranding';
import { toast } from '../lib/toast';
import Aurora from '../components/Aurora';
import PasswordInput from '../components/PasswordInput';
import logoFull from '../assets/logo-full.png';

export default function Login() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { logoUrl, name } = useBranding();
  const login = useAuth((s) => s.login);
  const error = useAuth((s) => s.error);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (ok) {
      toast.ok('Welcome back', 'Signing you in…');
      const dest = loc.state?.from?.pathname || '/';
      navigate(dest, { replace: true });
    }
  }

  return (
    <div className="login-shell">
      <Aurora />
      <article className="glass login-card">
        <div className="brand-row">
          {/* Tenant logo when configured; bundled default otherwise, and also
              if the configured URL fails to load (image deleted / moved). */}
          <img
            src={logoUrl || logoFull}
            alt={name || 'Neon Holidays'}
            onError={(e) => { e.currentTarget.src = logoFull; }}
          />
          <span className="brand-sub" style={{ borderBottom: 'none', padding: 0 }}>
            Travel-Tech Admin Console
          </span>
        </div>
        <h1>Sign in</h1>

        <form onSubmit={onSubmit}>
          {error ? <div className="err-banner">{error}</div> : null}
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="text"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <button className="btn primary" type="submit" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <p style={{ marginTop: 14, textAlign: 'center', fontSize: '.8rem' }}>
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
      </article>
    </div>
  );
}
